import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { CardImage } from './CardImage';

function buildSlides(images, videoUrl) {
  const imageUrls = Array.isArray(images) ? images.filter(Boolean) : [];
  const slides = imageUrls.map((src) => ({ type: 'image', src }));
  if (videoUrl) {
    slides.push({ type: 'video', src: videoUrl });
  }
  return slides;
}

function MediaFrame({ children, inactive, isDark, theme }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1 / 1',
        borderRadius: 3,
        overflow: 'hidden',
        background: isDark
          ? `linear-gradient(180deg, ${alpha('#3ab5b0', 0.12)} 0%, ${alpha('#1e1e1e', 0.9)} 100%)`
          : 'linear-gradient(180deg, rgba(58,181,176,0.08) 0%, #ffffff 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        filter: inactive ? 'grayscale(0.65) opacity(0.55)' : 'none',
        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.06)}`,
      }}
    >
      {children}
    </Box>
  );
}

function VideoSlide({ src, inactive, isDark, theme }) {
  return (
    <MediaFrame inactive={inactive} isDark={isDark} theme={theme}>
      <Box
        component="video"
        src={src}
        controls
        playsInline
        preload="metadata"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          background: 'linear-gradient(145deg, #1f6e6b 0%, #2a938f 55%, #3ab5b0 100%)',
        }}
      />
    </MediaFrame>
  );
}

function VideoThumb({ selected, inactive }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        background: 'linear-gradient(145deg, #1f6e6b 0%, #2a938f 55%, #3ab5b0 100%)',
        opacity: inactive ? 0.55 : 1,
      }}
    >
      <PlayArrowRoundedIcon
        sx={{
          color: '#fff',
          fontSize: selected ? 28 : 24,
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.35))',
        }}
      />
    </Box>
  );
}

export default function ProductImageSlider({ images, videoUrl = null, alt, inactive = false }) {
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';
  const isDark = theme.palette.mode === 'dark';
  const slides = useMemo(() => buildSlides(images, videoUrl), [images, videoUrl]);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const slideKey = slides.map((s) => `${s.type}:${s.src}`).join('|');

  useEffect(() => {
    setIndex(0);
  }, [slideKey]);

  if (slides.length === 0) {
    return (
      <CardImage
        src={null}
        alt={alt}
        aspectRatio="1 / 1"
        rounded={3}
        iconSize={48}
        fit="contain"
        inactive={inactive}
        bordered={false}
      />
    );
  }

  if (slides.length === 1 && slides[0].type === 'image') {
    return (
      <CardImage
        src={slides[0].src}
        alt={alt}
        aspectRatio="1 / 1"
        rounded={3}
        iconSize={48}
        fit="contain"
        inactive={inactive}
        bordered={false}
      />
    );
  }

  if (slides.length === 1 && slides[0].type === 'video') {
    return <VideoSlide src={slides[0].src} inactive={inactive} isDark={isDark} theme={theme} />;
  }

  const safeIndex = Math.min(index, slides.length - 1);
  const current = slides[safeIndex];
  const goTo = (next) => {
    setIndex(((next % slides.length) + slides.length) % slides.length);
  };

  const PrevIcon = isRtl ? ChevronRightIcon : ChevronLeftIcon;
  const NextIcon = isRtl ? ChevronLeftIcon : ChevronRightIcon;

  const navButtonSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    width: { xs: 36, sm: 42 },
    height: { xs: 36, sm: 42 },
    bgcolor: isDark ? alpha('#1e1e1e', 0.88) : alpha('#fff', 0.94),
    border: 1,
    borderColor: isDark ? alpha('#fff', 0.12) : 'divider',
    color: 'primary.main',
    boxShadow: isDark
      ? '0 4px 16px rgba(0,0,0,0.35)'
      : '0 4px 14px rgba(42,147,143,0.18)',
    '&:hover': {
      bgcolor: isDark ? alpha('#1e1e1e', 0.98) : '#fff',
      borderColor: 'primary.light',
    },
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          borderRadius: 3,
          overflow: 'hidden',
        }}
        onTouchStart={(e) => {
          touchStartX.current = e.changedTouches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null) return;
          const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
          const delta = endX - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(delta) < 40) return;
          if (isRtl) {
            goTo(safeIndex + (delta > 0 ? 1 : -1));
          } else {
            goTo(safeIndex + (delta > 0 ? -1 : 1));
          }
        }}
      >
        {current.type === 'video' ? (
          <VideoSlide src={current.src} inactive={inactive} isDark={isDark} theme={theme} />
        ) : (
          <CardImage
            src={current.src}
            alt={`${alt} (${safeIndex + 1}/${slides.length})`}
            aspectRatio="1 / 1"
            rounded={3}
            iconSize={48}
            fit="contain"
            inactive={inactive}
            bordered={false}
            padded
          />
        )}

        <IconButton
          aria-label="previous image"
          onClick={() => goTo(safeIndex - 1)}
          sx={{ ...navButtonSx, insetInlineStart: 8 }}
        >
          <PrevIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="next image"
          onClick={() => goTo(safeIndex + 1)}
          sx={{ ...navButtonSx, insetInlineEnd: 8 }}
        >
          <NextIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.6,
            borderRadius: 999,
            bgcolor: alpha('#0d3332', isDark ? 0.55 : 0.38),
            backdropFilter: 'blur(6px)',
          }}
        >
          {slides.map((slide, i) => (
            <Box
              key={`${slide.type}-${i}`}
              component="button"
              type="button"
              aria-label={slide.type === 'video' ? 'video' : `image ${i + 1}`}
              onClick={() => setIndex(i)}
              sx={{
                width: i === safeIndex ? 18 : 8,
                height: 8,
                border: 0,
                p: 0,
                borderRadius: 999,
                cursor: 'pointer',
                bgcolor: i === safeIndex ? '#fff' : alpha('#fff', 0.45),
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        justifyContent="center"
        sx={{
          mt: 1.75,
          overflowX: 'auto',
          pb: 0.5,
          px: 0.25,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha(theme.palette.primary.main, 0.35),
            borderRadius: 999,
          },
        }}
      >
        {slides.map((slide, i) => {
          const selected = i === safeIndex;
          return (
            <Box
              key={`${slide.type}-${slide.src}-${i}`}
              component="button"
              type="button"
              onClick={() => setIndex(i)}
              aria-label={slide.type === 'video' ? 'video thumbnail' : `thumbnail ${i + 1}`}
              sx={{
                flex: '0 0 auto',
                width: { xs: 58, sm: 68 },
                height: { xs: 58, sm: 68 },
                p: slide.type === 'video' ? 0 : 0.6,
                borderRadius: 2,
                border: 2,
                borderColor: selected ? 'primary.main' : 'divider',
                background: isDark
                  ? alpha('#fff', 0.04)
                  : 'linear-gradient(180deg, rgba(58,181,176,0.08) 0%, #fff 100%)',
                cursor: 'pointer',
                overflow: 'hidden',
                boxShadow: selected
                  ? `0 4px 14px ${alpha(theme.palette.primary.main, 0.28)}`
                  : 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                transform: selected ? 'translateY(-1px)' : 'none',
              }}
            >
              {slide.type === 'video' ? (
                <VideoThumb selected={selected} inactive={inactive} />
              ) : (
                <Box
                  component="img"
                  src={slide.src}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    opacity: inactive ? 0.55 : 1,
                    filter: inactive ? 'grayscale(0.65)' : 'none',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
